import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SeasonSwitcher } from './SeasonSwitcher';
import VoiceJournalFAB from './voice-journal/VoiceJournalFAB';
import { 
  LayoutDashboard, 
  Users, 
  Trophy, 
  Calendar, 
  Layers, 
  Medal, 
  Settings, 
  Menu,
  X,
  PiggyBank,
  Table,
  Gavel,
  Plus,
  Minus,
  DoorOpen,
  UserPlus,
  User,
  Crown,
  PenTool,
  MessageSquare,
  ListTodo,
  BookOpen,
  Heart,
  Sparkles,
  NotebookPen,
  MessagesSquare
} from 'lucide-react';

// All navigation items with their page IDs
const allNavItems = [
  { path: '/clubhouse', label: 'Clubhouse', icon: LayoutDashboard, id: 'clubhouse', alwaysVisible: true, order: 0 },
  { path: '/players', label: 'Players', icon: Users, id: 'players', alwaysVisible: false, parent: 'dressing-room' },
  { path: '/matches', label: 'Matches', icon: Trophy, id: 'matches', alwaysVisible: false },
  { path: '/training', label: 'Training', icon: Calendar, id: 'training', alwaysVisible: false },
  { path: '/team-chat', label: 'Team Chat', icon: MessagesSquare, id: 'team-chat', alwaysVisible: false, adminOnly: true },
  { path: '/seasons', label: 'Seasons', icon: Layers, id: 'seasons', alwaysVisible: true, order: 100, adminOnly: true },
  { path: '/coaches-office', label: "Coach's Office", icon: Crown, id: 'coaches-office', alwaysVisible: false, adminOnly: true },
  { path: '/tactics-board', label: 'Tactics Board', icon: PenTool, id: 'tactics-board', alwaysVisible: false, parent: 'coaches-office' },
  { path: '/todo', label: 'To Do', icon: ListTodo, id: 'todo', alwaysVisible: false, parent: 'coaches-office' },
  { path: '/journal', label: 'Journal', icon: BookOpen, id: 'journal', alwaysVisible: false, parent: 'coaches-office' },
  { path: '/welfare', label: 'Welfare', icon: Heart, id: 'welfare', alwaysVisible: false, parent: 'coaches-office' },
  { path: '/ai-plans', label: 'AI Training Plans', icon: Sparkles, id: 'ai-plans', alwaysVisible: false, parent: 'coaches-office' },
  { path: '/match-notes', label: 'Match Notes', icon: NotebookPen, id: 'match-notes', alwaysVisible: false, parent: 'coaches-office' },
  { path: '/leaderboard', label: 'Leaderboard', icon: Medal, id: 'leaderboard', alwaysVisible: false, parent: 'dressing-room' },
  { path: '/league-table', label: 'League Table', icon: Table, id: 'league-table', alwaysVisible: false },
  { path: '/team-kitty', label: 'Team Kitty', icon: PiggyBank, id: 'team-kitty', alwaysVisible: false, parent: 'dressing-room' },
  { path: '/fines', label: 'Fines', icon: Gavel, id: 'fines', alwaysVisible: false, parent: 'dressing-room' },
  { path: '/forum', label: 'Coaches Forum', icon: MessageSquare, id: 'forum', alwaysVisible: true, order: 98 },
  { path: '/team-members', label: 'Team Members', icon: UserPlus, id: 'team-members', alwaysVisible: true, order: 99, adminOnly: true },
  { path: '/settings', label: 'Settings', icon: Settings, id: 'settings', alwaysVisible: true, order: 101, adminOnly: true },
  { path: '/my-profile', label: 'My Profile', icon: User, id: 'my-profile', alwaysVisible: true, order: 102, playerOnly: true },
];

// Parent menu items (non-linking, expandable)
const parentMenuItems = [
  { id: 'dressing-room', label: 'Dressing Room', icon: DoorOpen }
];

// Children IDs for each parent
const DRESSING_ROOM_CHILDREN = ['players', 'leaderboard', 'team-kitty', 'fines'];
const COACHES_OFFICE_CHILDREN = ['tactics-board', 'todo', 'journal', 'welfare', 'ai-plans', 'match-notes'];

const DEFAULT_PAGE_ORDER = ['players', 'matches', 'training', 'team-chat', 'leaderboard', 'league-table', 'team-kitty', 'fines', 'coaches-office', 'tactics-board', 'todo', 'journal', 'welfare', 'ai-plans', 'match-notes'];

// Helper to merge saved page order with any new pages
const mergePageOrder = (savedOrder, defaultOrder) => {
  if (!savedOrder || savedOrder.length === 0) return defaultOrder;
  
  // Find pages in default that aren't in saved order
  const newPages = defaultOrder.filter(pageId => !savedOrder.includes(pageId));
  
  // Add new pages at the end
  return [...savedOrder, ...newPages];
};

export const Layout = ({ children }) => {
  const { user, currentSeasonName, isAdmin, isPlayer } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [expandedMenus, setExpandedMenus] = useState([]); // Start collapsed

  // Get custom menu color or use default
  const menuColor = user?.menu_color || '#064E3B';
  const sidebarBgImage = user?.sidebar_bg_image || '';
  const sidebarBgOpacity = user?.sidebar_bg_opacity ?? 0.3;
  const sidebarBgOffset = user?.sidebar_bg_offset ?? 0;
  
  // Get hidden pages and page order from user settings (merge with new pages)
  const hiddenPages = user?.hidden_pages || [];
  const pageOrder = mergePageOrder(user?.page_order, DEFAULT_PAGE_ORDER);
  
  // Check if current page is a dressing room child - if so, keep menu expanded
  const isDressingRoomChildActive = DRESSING_ROOM_CHILDREN.some(childId => {
    const childItem = allNavItems.find(item => item.id === childId);
    return childItem && location.pathname === childItem.path;
  });
  const isCoachesOfficeChildActive = COACHES_OFFICE_CHILDREN.some(childId => {
    const childItem = allNavItems.find(item => item.id === childId);
    return childItem && location.pathname === childItem.path;
  });

  // Auto-expand dressing room ONLY when navigating to a child page
  // NEVER auto-collapse - that should only happen via manual user click
  useEffect(() => {
    if (isDressingRoomChildActive) {
      setExpandedMenus(prev => {
        if (!prev.includes('dressing-room')) {
          return [...prev, 'dressing-room'];
        }
        return prev;
      });
    }
    if (isCoachesOfficeChildActive) {
      setExpandedMenus(prev => {
        if (!prev.includes('coaches-office')) {
          return [...prev, 'coaches-office'];
        }
        return prev;
      });
    }
    // Do NOT auto-collapse when navigating away - let user control that
  }, [isDressingRoomChildActive, isCoachesOfficeChildActive]);
  
  // Toggle expand/collapse for parent menu items
  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Check if any dressing room children are visible (not hidden)
  const dressingRoomChildren = DRESSING_ROOM_CHILDREN.filter(id => !hiddenPages.includes(id));
  const showDressingRoom = dressingRoomChildren.length > 0;
  const coachesOfficeChildren = COACHES_OFFICE_CHILDREN.filter(id => !hiddenPages.includes(id));
  const showCoachesOffice = coachesOfficeChildren.length > 0;
  
  // Build navigation items based on visibility, custom order, and user role
  const navItems = (() => {
    // Start with Clubhouse (always first)
    const items = [allNavItems.find(item => item.id === 'clubhouse')];
    
    // Add optional pages in custom order (if not hidden and not a dressing room child)
    pageOrder.forEach(pageId => {
      const item = allNavItems.find(i => i.id === pageId);
      if (item && !hiddenPages.includes(pageId) && !DRESSING_ROOM_CHILDREN.includes(pageId) && !COACHES_OFFICE_CHILDREN.includes(pageId)) {
        // Skip admin-only items for players
        if (item.adminOnly && isPlayer) return;
        // Skip player-only items for admins
        if (item.playerOnly && isAdmin) return;
        items.push(item);
      }
    });
    
    // Add role-specific items at the end
    const endItems = allNavItems
      .filter(item => item.order >= 97)
      .sort((a, b) => a.order - b.order);
    
    endItems.forEach(item => {
      // Skip admin-only items for players
      if (item.adminOnly && isPlayer) return;
      // Skip player-only items for admins
      if (item.playerOnly && isAdmin) return;
      // Skip hidden pages
      if (hiddenPages.includes(item.id)) return;
      items.push(item);
    });
    
    return items.filter(Boolean);
  })();

  // Get dressing room child items in order
  // Get dressing room child items in the user's custom order
  const getDressingRoomItems = () => {
    // Filter dressing room children that are not hidden
    const visibleChildren = DRESSING_ROOM_CHILDREN.filter(id => !hiddenPages.includes(id));
    
    // Sort by the user's custom page order
    return visibleChildren
      .sort((a, b) => {
        const indexA = pageOrder.indexOf(a);
        const indexB = pageOrder.indexOf(b);
        // If not in pageOrder, put at end
        const posA = indexA === -1 ? 999 : indexA;
        const posB = indexB === -1 ? 999 : indexB;
        return posA - posB;
      })
      .map(id => allNavItems.find(item => item.id === id))
      .filter(Boolean);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen, isMobile]);

  const handleOpenMenu = () => {
    setSidebarOpen(true);
  };

  const handleCloseMenu = () => {
    setSidebarOpen(false);
  };

  const SidebarContent = ({ closeSidebar }) => {
    // When no season is active, the sidebar header is in "Coach Profile" mode
    // — show the coach's role under their name instead of "No Season Selected".
    const noSeason = !user?.current_season_id;
    const seasonDisplayText = currentSeasonName
      ? currentSeasonName
      : (noSeason ? (user?.current_role || '') : '');

    return (
    <div className="flex flex-col h-full">
      {/* Logo/Team Section */}
      <div className="p-6">
        <div className="flex items-center gap-3">
          {(noSeason ? user?.profile_photo : user?.club_badge) ? (
            <img 
              src={(() => {
                const src = noSeason ? user.profile_photo : user.club_badge;
                return src.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
              })()} 
              alt={noSeason ? 'Profile photo' : 'Club Badge'}
              className={`w-12 h-12 object-cover ${noSeason ? 'rounded-full' : 'rounded-xl'}`}
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white/70" strokeWidth={1.5} />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-white font-title tracking-wide">
              {noSeason
                ? (user?.display_name || user?.name || 'Your Name')
                : (user?.team_name || 'My Team')}
            </h1>
            <div className="mt-0.5 flex items-center gap-1 min-w-0">
              {isAdmin ? (
                <SeasonSwitcher />
              ) : (
                <p className="text-xs text-white/70 h-4 truncate">{seasonDisplayText}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          // Insert Dressing Room after Clubhouse (index 0)
          const showDressingRoomHere = index === 0 && showDressingRoom;
          
          return (
            <div key={item.path}>
              {/* Coach's Office: link + expandable children */}
              {item.id === 'coaches-office' ? (
                <>
                  <div className="nav-item flex items-center justify-between" style={{ boxSizing: 'border-box' }}>
                    <Link
                      to={item.path}
                      onClick={closeSidebar}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[' ]/g, '-')}`}
                      className={`flex items-center gap-3 flex-1 ${isActive ? 'text-white' : ''}`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                    {showCoachesOffice && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleMenu('coaches-office'); }}
                        className="p-1 rounded hover:bg-white/10"
                        data-testid="nav-coaches-office-toggle"
                      >
                        {expandedMenus.includes('coaches-office') ? (
                          <Minus className="w-4 h-4 opacity-70" strokeWidth={2.5} />
                        ) : (
                          <Plus className="w-4 h-4 opacity-70" strokeWidth={2.5} />
                        )}
                      </button>
                    )}
                  </div>
                  {showCoachesOffice && (
                    <div className={`submenu-container ml-4 border-l border-white/20 ${expandedMenus.includes('coaches-office') ? 'submenu-expanded' : 'submenu-collapsed'}`}>
                      <div className="submenu-content">
                        {coachesOfficeChildren.map(childId => {
                          const childItem = allNavItems.find(n => n.id === childId);
                          if (!childItem) return null;
                          const ChildIcon = childItem.icon;
                          const isChildActive = location.pathname === childItem.path;
                          return (
                            <Link key={childItem.path} to={childItem.path} onClick={closeSidebar}
                              data-testid={`nav-${childItem.label.toLowerCase().replace(/[' ]/g, '-')}`}
                              className={`nav-item pl-4 ${isChildActive ? 'active' : ''}`}
                            >
                              <ChildIcon className="w-5 h-5" strokeWidth={1.5} />
                              <span className="font-medium">{childItem.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
              <Link
                to={item.path}
                onClick={closeSidebar}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </Link>
              )}
              
              {/* Dressing Room - inserted after Clubhouse */}
              {showDressingRoomHere && (
                <>
                  <div className="nav-item flex items-center justify-between" style={{ boxSizing: 'border-box' }}>
                    <Link
                      to="/dressing-room"
                      onClick={closeSidebar}
                      data-testid="nav-dressing-room"
                      className={`flex items-center gap-3 flex-1 ${location.pathname === '/dressing-room' ? 'text-white' : ''}`}
                    >
                      <DoorOpen className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="font-medium">Dressing Room</span>
                    </Link>
                    {dressingRoomChildren.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu('dressing-room');
                        }}
                        className="p-1 rounded hover:bg-white/10"
                      >
                        {expandedMenus.includes('dressing-room') ? (
                          <Minus className="w-4 h-4 opacity-70 flex-shrink-0" strokeWidth={2.5} />
                        ) : (
                          <Plus className="w-4 h-4 opacity-70 flex-shrink-0" strokeWidth={2.5} />
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Dressing Room Children */}
                  <div 
                    className={`submenu-container ml-4 border-l border-white/20 ${
                      expandedMenus.includes('dressing-room') ? 'submenu-expanded' : 'submenu-collapsed'
                    }`}
                  >
                    <div className="submenu-content">
                      {getDressingRoomItems().map((childItem) => {
                        const ChildIcon = childItem.icon;
                        const isChildActive = location.pathname === childItem.path;
                        return (
                          <Link
                            key={childItem.path}
                            to={childItem.path}
                            onClick={closeSidebar}
                            data-testid={`nav-${childItem.label.toLowerCase().replace(' ', '-')}`}
                            className={`nav-item pl-4 ${isChildActive ? 'active' : ''}`}
                          >
                            <ChildIcon className="w-5 h-5" strokeWidth={1.5} />
                            <span className="font-medium">{childItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );};

  return (
    <div className="app-layout overflow-hidden">
      {/* Mobile Header */}
      <header 
        className={`mobile-header ${sidebarOpen ? 'menu-open' : ''}`} 
        style={{ backgroundColor: menuColor }}
      >
        <div className="flex items-center gap-3">
          {(() => {
            const noSeason = !user?.current_season_id;
            const src = noSeason ? user?.profile_photo : user?.club_badge;
            if (src) {
              return (
                <img
                  src={src.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src}
                  alt={noSeason ? 'Profile photo' : 'Club Badge'}
                  className={`w-8 h-8 object-cover ${noSeason ? 'rounded-full' : 'rounded-lg'}`}
                />
              );
            }
            return (
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-white/70" strokeWidth={1.5} />
              </div>
            );
          })()}
          <h1 className="text-lg font-semibold text-white font-title">
            {user?.current_season_id
              ? (user?.team_name || 'My Team')
              : (user?.display_name || user?.name || 'Your Name')}
          </h1>
        </div>
        
        <button 
          className="text-white hover:bg-white/10 w-12 h-12 flex items-center justify-center rounded-md"
          data-testid="mobile-menu-btn"
          onClick={handleOpenMenu}
        >
          <Menu className="w-8 h-8" strokeWidth={2.5} />
        </button>
      </header>

      {/* Mobile Slide-in Menu with Push Animation */}
      {isMobile && (
        <>
          {/* Backdrop overlay */}
          <div 
            className={`mobile-backdrop ${sidebarOpen ? 'visible' : ''}`}
            onClick={handleCloseMenu}
            aria-hidden="true"
          />
          
          {/* Sliding sidebar */}
          <aside 
            className={`mobile-sidebar ${sidebarOpen ? 'open' : ''}`}
            style={{ backgroundColor: menuColor, overflow: 'hidden' }}
          >
            {sidebarBgImage && (
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage: `url(${sidebarBgImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: `center calc(100% + ${sidebarBgOffset}px)`,
                  backgroundRepeat: 'no-repeat',
                  opacity: sidebarBgOpacity,
                  maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 80%)',
                  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 80%)',
                }}
                data-testid="mobile-sidebar-bg-image"
              />
            )}
            {/* Close button */}
            <button
              onClick={handleCloseMenu}
              className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-lg transition-colors z-20"
              data-testid="mobile-menu-close-btn"
              aria-label="Close menu"
            >
              <X className="w-8 h-8" strokeWidth={2.5} />
            </button>
            
            <div className="relative z-10 h-full">
              <SidebarContent closeSidebar={handleCloseMenu} />
            </div>
          </aside>
        </>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside className="sidebar" style={{ backgroundColor: menuColor, overflow: 'hidden' }}>
          {sidebarBgImage && (
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: `url(${sidebarBgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: `center calc(100% + ${sidebarBgOffset}px)`,
                backgroundRepeat: 'no-repeat',
                opacity: sidebarBgOpacity,
                maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 80%)',
                WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 80%)',
              }}
              data-testid="sidebar-bg-image"
            />
          )}
          <div className="flex flex-col h-full relative z-10">
            <SidebarContent closeSidebar={() => {}} />
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main className={`main-content ${isMobile && sidebarOpen ? 'menu-open' : ''}`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Voice journal floating mic (admin-only, shown on coaching pages) */}
      <VoiceJournalFAB />
    </div>
  );
};
